import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Check, ArrowRight, X } from "lucide-react";
import { SERVICES } from "../lib/data";
import { PageHero } from "../components/PageHero";
import { FadeUp, SectionHead } from "../components/Reveal";
import { CtaSection } from "../components/CtaSection";
import { ShareButtons } from "../components/ShareButtons";

const FLYERS = [
  {
    src: "/flyers/akses-pendampingan-nusantara-dokumen.jpg",
    judul: "Akses Pendampingan Nusantara — Dokumen & Administrasi",
  },
  {
    src: "/flyers/akses-pendampingan-nusantara-perkara.jpg",
    judul: "Akses Pendampingan Nusantara — Perkara Pidana & Perdata",
  },
];

const ServiceRow = ({ service, open, onToggle, index }) => {
  const Icon = service.icon;
  return (
    <FadeUp delay={0.05 * index}>
      <div
        data-testid={`service-row-${service.id}`}
        className={`border-b border-white/10 transition-colors duration-500 ${
          open ? "bg-[#0d0d0d]" : "hover:bg-[#0a0a0a]"
        }`}
      >
        <button
          data-testid={`service-toggle-${service.id}`}
          onClick={onToggle}
          aria-expanded={open}
          className="flex w-full items-center gap-6 px-2 py-8 text-left lg:gap-12 lg:px-6"
        >
          <span className="font-serif text-xl italic text-gold/50 lg:text-2xl">
            {service.nomor}
          </span>
          <span className="flex-1 font-serif text-2xl text-[#F5F5F5] sm:text-3xl lg:text-4xl">
            {service.judul}
          </span>
          <Icon
            size={24}
            strokeWidth={1.5}
            className="hidden text-gold/70 sm:block"
          />
          <motion.span
            animate={{ rotate: open ? 45 : 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="flex h-10 w-10 shrink-0 items-center justify-center border border-gold/40 text-gold"
          >
            <Plus size={16} />
          </motion.span>
        </button>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <div className="grid gap-10 px-2 pb-10 lg:grid-cols-2 lg:px-6 lg:pl-[7.5rem]">
                <p className="text-base font-light leading-relaxed text-[#A3A3A3] md:text-lg">
                  {service.ringkasan}
                </p>
                <ul className="space-y-3">
                  {service.poin.map((p) => (
                    <li key={p} className="flex gap-3 text-sm font-light text-[#C9C9C9]">
                      <Check size={16} className="mt-0.5 shrink-0 text-gold" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </FadeUp>
  );
};

export default function Layanan() {
  const [openId, setOpenId] = useState(SERVICES[0].id);
  const [lightbox, setLightbox] = useState(null);

  return (
    <>
      <PageHero
        eyebrow="Layanan Hukum"
        titleLines={[
          "Apa pun perkaranya,",
          <span key="t" className="italic text-gold">
            ada jalan hukumnya.
          </span>,
        ]}
        description="Dari ruang sidang pidana hingga meja negosiasi bisnis — enam bidang praktik kami ditangani advokat yang berdedikasi pada satu hal: kepentingan Anda."
      />

      <section className="mx-auto w-full max-w-7xl px-6 pb-24 lg:px-10">
        <div className="border-t border-white/10">
          {SERVICES.map((s, i) => (
            <ServiceRow
              key={s.id}
              service={s}
              index={i}
              open={openId === s.id}
              onToggle={() => setOpenId(openId === s.id ? null : s.id)}
            />
          ))}
        </div>

        <FadeUp className="mt-16 flex flex-wrap items-center justify-between gap-6 border border-gold/30 bg-[#0d0d0d] p-8 lg:p-12">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.35em] text-gold">
              Bantuan Hukum Pro Bono
            </p>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-snug text-[#F5F5F5] lg:text-4xl">
              Tidak mampu membayar pengacara? Anda tetap berhak didampingi.
            </h2>
            <p className="mt-4 max-w-xl text-sm font-light leading-relaxed text-[#A3A3A3]">
              Sesuai UU No. 16 Tahun 2011, kami menyediakan bantuan hukum
              cuma-cuma bagi masyarakat tidak mampu. Cukup bawa surat
              keterangan tidak mampu dari kelurahan.
            </p>
          </div>
          <Link
            to="/kontak"
            data-testid="probono-cta-button"
            className="group inline-flex items-center gap-3 bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.2em] text-ink transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-light"
          >
            Ajukan Bantuan Hukum
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </section>

      <section className="border-t border-white/10 bg-[#0A0A0A]">
        <div className="mx-auto w-full max-w-7xl px-6 py-24 lg:px-10">
          <SectionHead
            eyebrow="Flyer Layanan"
            titleLines={[
              "Program layanan unggulan,",
              <span key="t" className="italic text-gold">
                siap dibagikan.
              </span>,
            ]}
            description="Klik flyer untuk melihat ukuran penuh, atau bagikan langsung ke media sosial Anda — bantu lebih banyak orang mengenal layanan bantuan hukum YLBH."
          />
          <div className="mt-14 grid gap-10 md:grid-cols-2">
            {FLYERS.map((f, i) => (
              <FadeUp key={f.src} delay={0.1 * i}>
                <figure
                  data-testid={`flyer-card-${i}`}
                  className="border border-white/10 bg-[#0d0d0d] p-4 transition-colors duration-500 hover:border-gold/40"
                >
                  <button
                    onClick={() => setLightbox(f)}
                    data-testid={`flyer-open-${i}`}
                    aria-label={`Perbesar flyer: ${f.judul}`}
                    className="group block w-full cursor-zoom-in overflow-hidden"
                  >
                    <motion.img
                      src={f.src}
                      alt={f.judul}
                      className="max-h-[480px] w-full object-contain"
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    />
                  </button>
                  <figcaption className="mt-5 space-y-4">
                    <p className="font-serif text-lg leading-snug text-[#F5F5F5]">{f.judul}</p>
                    <ShareButtons
                      url={`${window.location.origin}/layanan`}
                      title={`${f.judul} — YLBH Sumatera Timur`}
                      imageUrl={`${window.location.origin}${f.src}`}
                      testidPrefix={`flyer-share-${i}`}
                    />
                  </figcaption>
                </figure>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="flyer-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-black/92 p-4 backdrop-blur-sm"
          >
            <button
              onClick={() => setLightbox(null)}
              aria-label="Tutup pratinjau flyer"
              data-testid="flyer-lightbox-close"
              className="absolute right-5 top-5 border border-white/20 p-2 text-white transition-colors hover:border-gold hover:text-gold"
            >
              <X size={20} />
            </button>
            <motion.img
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              src={lightbox.src}
              alt={lightbox.judul}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[88vh] max-w-full object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>

      <CtaSection testid="layanan-cta" />
    </>
  );
}
